import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import { ChevronDownIcon, ChevronUpIcon } from '@shared/ui/icons';
import { useLayout } from '../model';
import { sx } from '../config';
import type { NavItem } from '../model';

function NavigationPopoverItem({
  item,
  depth = 0,
  onClose,
}: {
  item: NavItem;
  depth?: number;
  onClose?: () => void;
}) {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const hasChildren = !!item.children?.length;

  const handleClick = () => {
    if (hasChildren) {
      setExpanded((p) => !p);
    } else {
      if (item.path) {
        navigate(item.path);
        onClose?.();
      }
      item.onClick?.();
    }
  };

  return (
    <>
      <ListItem disablePadding sx={{ display: 'block' }}>
        <ListItemButton
          onClick={handleClick}
          sx={{ minHeight: 40, pl: 2 + depth * 2, pr: 2 }}
        >
          {item.icon && (
            <ListItemIcon sx={{ minWidth: 0, mr: 2 }}>{item.icon}</ListItemIcon>
          )}
          <ListItemText primary={item.label} />
          {hasChildren && (expanded ? <ChevronDownIcon /> : <ChevronUpIcon />)}
        </ListItemButton>
      </ListItem>
      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List disablePadding>
            {item.children!.map((child) => (
              <NavigationPopoverItem
                key={child.label}
                item={child}
                depth={depth + 1}
                onClose={onClose}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

export function NavigationItem({ item, depth }: { item: NavItem; depth: number }) {
  const navigate = useNavigate();
  const location = useLocation();
  const layout = useLayout();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const hasChildren = !!item.children?.length;
  const expanded = !!layout.expandedItems[item.label];
  const isActive =
    !!item.path &&
    (location.pathname === item.path || location.pathname.startsWith(item.path + '/'));

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (hasChildren && !layout.isDrawerOpen) {
      setAnchorEl(e.currentTarget);
    } else if (hasChildren && layout.isDrawerOpen) {
      layout.toggleItem(item.label);
      if (item.path) navigate(item.path);
    } else {
      if (item.path) navigate(item.path);
      item.onClick?.();
    }
  };

  return (
    <Box>
      <ListItem
        disablePadding
        sx={{
          display: 'block',
          pl: layout.isDrawerOpen ? depth * 3.5 : 0,
          transition: 'padding-left 300ms',
        }}
      >
        <Tooltip
          title={item.label}
          placement="right"
          disableHoverListener={layout.isDrawerOpen}
          disableFocusListener={layout.isDrawerOpen}
          disableTouchListener={layout.isDrawerOpen}
        >
          <ListItemButton
            onClick={handleClick}
            sx={[
              {
                height: 36,
                px: 1.5,
                py: 0,
                backgroundColor: isActive ? '#84D300' : 'transparent',
                borderRadius: 1,
                '&:hover': { backgroundColor: isActive ? '#84D300' : '#262626' },
              },
              layout.isDrawerOpen
                ? { justifyContent: 'initial' }
                : { justifyContent: 'center' },
            ]}
          >
            <ListItemIcon
              sx={[
                {
                  minWidth: 0,
                  justifyContent: 'center',
                  '& .MuiSvgIcon-root': { color: isActive ? '#1A1A1A' : '#888888' },
                  transition: 'margin 300ms',
                },
                layout.isDrawerOpen ? { mr: 1 } : { mr: 0 },
              ]}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              slotProps={{
                primary: {
                  sx: {
                    fontSize: 14,
                    fontWeight: 600,
                    color: isActive ? '#1A1A1A' : '#E7E7E7',
                  },
                },
              }}
              sx={[
                { transition: 'opacity 0.5s ease-in-out, clip-path 0.5s ease-in-out' },
                layout.isDrawerOpen
                  ? { opacity: 1, clipPath: 'inset(0 0% 0 0)' }
                  : { opacity: 0, clipPath: 'inset(0 100% 0 0)' },
              ]}
            />
            {hasChildren &&
              layout.isDrawerOpen &&
              (expanded ? (
                <ChevronDownIcon sx={[sx, { color: isActive ? '#1A1A1A' : '#888888' }]} />
              ) : (
                <ChevronUpIcon sx={[sx, { color: isActive ? '#1A1A1A' : '#888888' }]} />
              ))}
          </ListItemButton>
        </Tooltip>
      </ListItem>

      {hasChildren && layout.isDrawerOpen && (
        <Box sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              left: `${depth * 28 + 22}px`,
              top: 0,
              bottom: 0,
              width: '1px',
              bgcolor: expanded ? '#262626' : 'transparent',
              borderRadius: 1,
              transition: 'background-color 300ms',
            }}
          />
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <List
              disablePadding
              sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
            >
              {item.children!.map((child) => (
                <NavigationItem key={child.label} item={child} depth={depth + 1} />
              ))}
            </List>
          </Collapse>
        </Box>
      )}

      {hasChildren && !layout.isDrawerOpen && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          disableRestoreFocus
          slotProps={{ paper: { sx: { minWidth: 200 } } }}
        >
          <List disablePadding>
            {item.children!.map((child) => (
              <NavigationPopoverItem
                key={child.label}
                item={child}
                onClose={() => setAnchorEl(null)}
              />
            ))}
          </List>
        </Popover>
      )}
    </Box>
  );
}

export function NavigationItemsList({ navItems }: { navItems: NavItem[] }) {
  return (
    <List
      disablePadding
      sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 1.5, px: 2 }}
    >
      {navItems.map((item) => (
        <NavigationItem key={item.label} item={item} depth={0} />
      ))}
    </List>
  );
}
